import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './BestSelling.css';

const NO_IMAGE_PLACEHOLDER = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPSczMDAnIGhlaWdodD0nMzAwJyB2aWV3Qm94PScwIDAgMzAwIDMwMCc+PHJlY3Qgd2lkdGg9JzEwMCUnIGhlaWdodD0nMTAwJScgZmlsbD0nI2YxZjVmOScvPjx0ZXh0IHg9JzUwJScgeT0nNTAlJyBkb21pbmFudC1iYXNlbGluZT0nbWlkZGxlJyB0ZXh0LWFuY2hvcj0nbWlkZGxlJyBmb250LWZhbWlseT0nc2Fucy1zZXJpZicgZm9udC1zaXplPScxNicgZmlsbD0nIzk0YTNiOCc+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+";

const BestSelling = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [videoProducts, setVideoProducts] = useState([]);

  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const envApiUrl = process.env.REACT_APP_API_URL?.trim();
  const API_URL = envApiUrl || '/api';

  const fetchConfig = useMemo(() => ({
    apiUrl: API_URL
  }), [API_URL]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${fetchConfig.apiUrl}/products?limit=6&sort=-createdAt`);
        const json = await res.json();

        if (res.ok && json.success && Array.isArray(json.data) && json.data.length > 0) {
          setProducts(json.data);
          return;
        }
      } catch (error) {
        console.error('Best selling products fetch failed:', error);
      }
      setProducts([]);
    };

    fetchProducts();
  }, [fetchConfig]);

  useEffect(() => {
    const fetchVideoProducts = async () => {
      try {
        const res = await fetch(`${fetchConfig.apiUrl}/products?all=true&limit=1000`);
        const json = await res.json();
        if (res.ok && json.success && Array.isArray(json.data)) {
          const filtered = json.data.filter(p => {
            if (!p.videoUrl) return false;
            const id = getYouTubeId(p.videoUrl);
            return !!id;
          });
          setVideoProducts(filtered.slice(0, 3));
        }
      } catch (err) {
        console.error('Error fetching video products:', err);
      }
    };
    fetchVideoProducts();
  }, [fetchConfig]);

  const getImage = (product) => {
    const imgName = product?.images?.[0] || product?.img;

    if (!imgName) return NO_IMAGE_PLACEHOLDER;
    if (imgName.startsWith('http://') || imgName.startsWith('https://') || imgName.startsWith('data:')) {
      return imgName;
    }

    const cleanImgName = (imgName.startsWith('/uploads/') || imgName.startsWith('uploads/')) ? imgName : `/uploads/${imgName}`;
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.') || window.location.hostname.startsWith('172.') || window.location.hostname.startsWith('10.') || window.location.port !== '';
    let backendBase = isLocalHost ? 'http://localhost:5000' : fetchConfig.apiUrl.replace('/api', '');
    if (!backendBase || backendBase.startsWith('/')) {
      backendBase = `${window.location.protocol}//${window.location.hostname}:5000`;
    }
    return `${backendBase}${cleanImgName.startsWith('/') ? '' : '/'}${cleanImgName}`;
  };

  if (!products || products.length === 0) return null;

  return (
    <section className="best-selling">
      <div className="container">
        <div className="section-header">
          <span className="badge-tag orange-pill">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 21h8"></path><path d="M12 17v4"></path><path d="M7 4h10"></path><path d="M17 4v8a5 5 0 0 1-10 0V4"></path><path d="M7 9H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><path d="M17 9h2a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2"></path></svg>
            {t('best_selling.badge')}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path></svg>
          </span>
          <h2 className="section-title gradient-text">{t('best_selling.title')}</h2>
          <p className="section-subtitle">
            {t('best_selling.subtitle')}
          </p>
          
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-icon star">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
              </div>
              <div className="stat-text">
                <span className="stat-value">4.9</span>
                <span className="stat-label">{t('best_selling.avg_rating')}</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon users">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              </div>
              <div className="stat-text">
                <span className="stat-value">50K+</span>
                <span className="stat-label">{t('best_selling.happy_farmers')}</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon lightning">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
              </div>
              <div className="stat-text">
                <span className="stat-value">{t('best_selling.delivery_time_val', 'On Time')}</span>
                <span className="stat-label">{t('best_selling.fast_delivery')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="products-grid">
          {products.slice(0, 6).map((prod, index) => (
            <div key={prod._id || prod.slug || index} className="product-card">
              <div className="product-image" style={{ backgroundImage: `url(${getImage(prod, index)})` }}>
                <div className="badge-top-left">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 21h8"></path><path d="M12 17v4"></path><path d="M7 4h10"></path><path d="M17 4v8a5 5 0 0 1-10 0V4"></path><path d="M7 9H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><path d="M17 9h2a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2"></path></svg>
                  #{index + 1} {t('best_selling.best_seller')}
                </div>
                <div className="badge-top-right">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path></svg>
                </div>
              </div>
              <div className="product-info">
                <h3 className="product-name">{prod.name}</h3>
                <div className="product-actions">
                  <button className="btn-details" onClick={() => navigate(prod.slug ? `/product/${prod.slug}` : (prod.path || '/products'))}>{t('best_selling.view_details')}</button>
                  <button className="btn-enquire" onClick={() => navigate(`/enquire?product=${prod.slug || prod._id}&productName=${encodeURIComponent(prod.name)}`)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '5px'}}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l2.27-2.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                    {t('best_selling.enquire_now')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="actions">
          <button className="btn-view-all" onClick={() => navigate('/products')}>
            {t('best_selling.view_all')}
          </button>
        </div>

        {/* YouTube Video Section */}
        {videoProducts && videoProducts.length > 0 && (
          <div className="homepage-videos-section" style={{ marginTop: '70px', borderTop: '1px solid #f0f0f0', paddingTop: '60px' }}>
            <h3 className="section-title" style={{ fontSize: '32px', marginBottom: '15px' }}>
              {t('best_selling.watch_videos_title', 'Watch Our Equipment in Action')}
            </h3>
            <p className="section-subtitle" style={{ marginBottom: '45px', maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto' }}>
              {t('best_selling.watch_videos_sub', 'Check out video demonstrations of our high-quality agricultural machinery')}
            </p>
            <div className="videos-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '30px',
              marginTop: '20px'
            }}>
              {videoProducts.map((prod) => {
                const videoId = getYouTubeId(prod.videoUrl);
                return (
                  <div key={prod._id} className="video-card-wrapper" style={{
                    background: '#fff',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.03)',
                    border: '1px solid #f0f0f0',
                    transition: 'all 0.3s ease'
                  }}>
                    <div className="video-iframe-container" style={{
                      position: 'relative',
                      paddingBottom: '56.25%', // 16:9 Aspect Ratio
                      height: 0,
                      overflow: 'hidden'
                    }}>
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title={prod.name}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; compute-pressure"
                        allowFullScreen
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%'
                        }}
                      ></iframe>
                    </div>
                    <div style={{ padding: '18px', textAlign: 'left' }}>
                      <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#2c3e50', textTransform: 'capitalize' }}>
                        {prod.name}
                      </h4>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default BestSelling;


